/*
 * BuroEcl
 * Copyright (c) 2023 Buroclic. All rights reserved.
 * 
 * This file corresponds to a component allowing the user to
 * select a single option between several one but within a much more beautiful component
 */

// Imports
import React, { useEffect, useState, useRef } from 'react';
import PropTypes from "prop-types";
import { faAngleDown, faAngleUp } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

export const BCInput = (props) => {

    const value: any = props.value;
    const options :any[] = props.options;

    // Display state
    const [displayList, setDisplayList] = useState(props.expanded);
    useEffect(() => {
        props.setExpanded(displayList);
    }, [displayList, props]);

    // Option display Format 
    const keysToReplace : string[] = [];
    const matches :RegExpExecArray = props.optionDisplayString.matchAll(/\{(\w+)(.*?)\}/g);
    for (const match of matches) {
        const key = match[1];
        const content = match[2];
        keysToReplace[key] = content;
    }

    // get formated content for a specific option
    const getFormatedContent = (option: any) => {
        let content: string = '';

        Object.keys(keysToReplace).forEach((key) => {
            if (option[key] && option[key].length > 0)
                content += `${option[key]}${keysToReplace[key]}`;
        });

        return content;
    }

    // References
    const parentElement = useRef<HTMLInputElement>(null); // HTML Element for Folder selection (based on search)
    const optionsList = useRef<HTMLUListElement | null>(null); // HTML Element for Listing folder (based on search)


    // Hide the options if user click somewhere else
    useEffect(() => {
        const handleExitingInput = (event) => {       
            // Hide options if the focused element is neither the input nor the list of folder nor a <li> in list
            if (event.target !== parentElement.current && event.target.parentElement !== parentElement.current && event.target !== optionsList.current && event.target.parentElement !== optionsList.current)
                setDisplayList(false);
        }

        // Add/Remove the listener
        document.addEventListener('click', handleExitingInput);
        return () => { document.removeEventListener('click', handleExitingInput); }
    }, []);



    // Handle arrowdown / arrowup / enter keyboard input inside the custom input
    const handleKeyboardManagement = (event) => {
        if (event.key === "ArrowDown") {
            if (event.target === parentElement.current) (optionsList.current?.children[0] as HTMLLIElement)?.focus();
            else if (event.target.parentElement === optionsList.current) (event.target.nextElementSibling as HTMLLIElement)?.focus();
        }
        else if (event.key === "ArrowUp")
            if (event.target === optionsList.current?.children[0]) (parentElement.current?.focus())
            else if (event.target.parentElement === optionsList.current) (event.target.previousSibling as HTMLLIElement)?.focus();
        else if (event.key === "Enter")
            (document.activeElement as HTMLLIElement)?.click(); // simulate a click
    }


    // HTML Template
    return (
        <div className={`${props.className || ''}`}>
            <div className='border border-gray-300 text-md rounded-sm h-10 shadow-bottom text-left flex flex-row w-full cursor-pointer' ref={parentElement} tabIndex={0}
                onClick={() => setDisplayList(!displayList)} onKeyDown={handleKeyboardManagement}>
                <div className='flex-grow h-full p-2'>{getFormatedContent(value)}</div>
                <FontAwesomeIcon className={`w-6 h-6 mr-2 my-auto`} icon={!displayList ? faAngleDown : faAngleUp} />
            </div>

            {displayList && (
                <ul id="dossierList" className={`absolute bg-white border rounded-sm border-gray-300 z-40 w-[inherit] h-auto overflow-y-auto`} ref={optionsList}>
                    {/* Default option */}
                    {props.defaultOption && <li key='dossieroption-default' tabIndex={0} className="p-2 cursor-pointer text-md hover:bg-gray-100 focus:bg-gray-100"
                            onClick={() => { props.setValue(props.defaultOption); setDisplayList(false); }} onKeyDown={handleKeyboardManagement}>
                            {getFormatedContent(props.defaultOption)}
                        </li>}

                    {/* Browse options */}
                    {options.map((option, index) => (
                        <li key={'dossieroption-' + index} tabIndex={0} className="p-2 cursor-pointer text-md hover:bg-gray-100 focus:bg-gray-100"
                            onClick={() => { props.setValue(option); setDisplayList(false); }} onKeyDown={handleKeyboardManagement}>
                            {getFormatedContent(option)}
                        </li>
                    ))}
                </ul>)}
        </div>
    );
};

BCInput.propTypes = {
    value: PropTypes.any.isRequired,
    setValue: PropTypes.func.isRequired,
    options: PropTypes.arrayOf(PropTypes.any).isRequired,
    optionDisplayString: PropTypes.string.isRequired,
    defaultOption: PropTypes.any,

    expanded: PropTypes.bool,
    setExpanded: PropTypes.func,

    className: PropTypes.string,
}